import { resolveTypeElements } from "vue/compiler-sfc";
import { OrderStatus, OrderStatusType, ReturnStatus } from "~/services/utils";
import type { OmsOrderDetail, OmsOrderItem, OrderGeneratedResult, OrderParam } from "~/types/ConfirmOrder";
import type { PmsComment } from "~/types/ProductDetails";

export const useOrderStore = defineStore('order', () => {


    const status = ref()
    const days = ref()
    const orderRepo = orderRepository()
    const PAGE_SIZE = 10
    const errorLoading = ref()
    const orderGeneratedDetails = ref<OrderGeneratedResult>()
    // const confirmOrderResult = ref()
    // const selectedOrder = ref()
    const orderParams = ref<Record<string, any>>({
        pageNum: 1,
        pageSize: PAGE_SIZE,
    })

    function reset() {

        errorLoading.value = undefined
        orderParams.value = {
            pageNum: 1,
            pageSize: PAGE_SIZE,
            status: -1
        }
        orderGeneratedDetails.value = undefined
        // confirmOrderResult.value = undefined
        // selectedOrder.value = undefined
    }


    async function generateOrder(orderParam: OrderParam) {
        try {
            const data = await orderRepo.generateOrder(orderParam)
            if (data !== null && data.code === 200) {
                orderGeneratedDetails.value = data.data
                errorLoading.value = undefined
                return 'ok'
            } else {
                errorLoading.value = 'Error during generating order ' + data.code
                console.error('Failed to generate order:', data.message);
                return 'Error during generating order ' + data.code
            }
        } catch (reason) {
            errorLoading.value = 'Error during generating order: ' + reason
            console.error('Error during generating order:', reason);
            return 'Error during generating order: ' + reason
        }
    }



    // async function generateConfirmOrder(cartIds: number[]) {
    //     try {
    //         const data = await orderRepo.generateConfirmOrder(cartIds)
    //         if (data !== null && data.code === 200) {
    //             confirmOrderResult.value = data.data
    //             errorLoading.value = undefined
    //         } else {
    //             errorLoading.value = 'Error during generating order ' + data.code
    //             console.error('Failed to generate order:', data.message);
    //         }
    //     } catch (reason) {
    //         errorLoading.value = 'Error during generating order: ' + reason
    //         console.error('Error during generating order:', reason);
    //     }
    // }

    async function payOrderSuccess(orderId: number, payType: number) {
        try {
            const data = await orderRepo.payOrderSuccess(orderId, payType)
            if (data !== null && data.code === 200) {
                errorLoading.value = undefined
                return 'ok'
            } else {
                errorLoading.value = 'Error during paying order ' + data.code
                console.error('Failed to paying order:', data.message);
            }
        } catch (reason) {
            errorLoading.value = 'Error during paying order: ' + reason
            console.error('Error during paying order:', reason);
        }
    }

    async function cancelUserOrder(orderId: number) {
        try {
            const data = await orderRepo.cancelUserOrder(orderId)
            if (data !== null && data.code === 200) {
                errorLoading.value = undefined
                return 'ok'
            } else {
                errorLoading.value = 'Error during canceling order ' + data.code
                console.error('Failed to cancel order:', data.message);
            }
        } catch (reason) {
            errorLoading.value = 'Error during canceling order: ' + reason
            console.error('Error during canceling order:', reason);
        }
    }

    async function confirmReceiveOrder(orderId: number) {
        try {
            const data = await orderRepo.confirmReceiveOrder(orderId)
            if (data !== null && data.code === 200) {
                errorLoading.value = undefined
                return 'ok'
            } else {
                errorLoading.value = 'Error during confirming order ' + data.code
                console.error('Failed to confirm order:', data.message);
            }
        } catch (reason) {
            errorLoading.value = 'Error during confirming order: ' + reason
            console.error('Error during confirming order:', reason);
        }
    }

    function isOrderReturnable(order: OmsOrderItem): boolean {
        if (order.statusType === OrderStatusType.DELIVERY) {
            if (order.status === OrderStatus.delivered || order.status === OrderStatus.completed) {
                return true;
            }
        }

        return false
    }

    /*************  ✨ Codeium Command ⭐  *************/
    /**
     * Retrieves the rating for a specific product within an order.
     *
     * @param order - The order containing the list of comments.
     * @param productId - The unique identifier of the product to find the rating for.
     * @returns The star rating of the product if found; otherwise, returns 0.
     */

    /******  f7ec2a12-38a3-4167-be54-0350b618971c  *******/

    function getProductRating(order?: OmsOrderDetail, productId?: number): PmsComment | undefined {

        const item = order?.commentList?.findLast((element) => element.productId === productId);

        if (item) {
            return item;
        }

    }

    function isOrderCommentable(order: OmsOrderItem): boolean {
        if (order.statusType === OrderStatusType.DELIVERY) {
            if (order.status === OrderStatus.delivered || order.status === OrderStatus.completed) {
                return true;
            }
        } else if (order.statusType === OrderStatusType.RETURN) {
            return true;
        }

        return false
    }

    function isOrderCancelable(order: OmsOrderDetail, orderItemId: number): boolean {
        const item = order?.orderItemList?.find((element) => element.id === orderItemId);

        // Return false if item is not found or if the status type is completed or delivered
        if (!item || item.status === OrderStatus.completed || item.status === OrderStatus.delivered || item.status === OrderStatus.closed) {
            return false;
        }

        if (item.statusType !== OrderStatusType.DELIVERY) {
            return false
        }
        if (item.status >= OrderStatus.pendingShipment) {
            return true;
        }

        const history = order?.orderTimeLineList?.filter((element) => element.orderId === orderItemId);

        let maxOrderStatus = -1;
        let orderTime: Date | undefined;

        history?.forEach((element) => {
            if (element.productId === item.productId?.toString()) {
                if (element.orderStatus > maxOrderStatus) {
                    maxOrderStatus = element.orderStatus;
                    orderTime = element.createTime;
                }
            }
        });

        // Check if the order is still within the cancelation window
        if (item.statusType === OrderStatusType.DELIVERY) {
            const nowDate = new Date();
            if (orderTime) {
                const difference = Math.floor((nowDate.getTime() - orderTime.getTime()) / (1000 * 3600 * 24)); // in days
                if (difference < 7) {
                    return true;
                }
            }
        }

        return false;
    }


    function getEstimatedDate(item: OmsOrderItem, orderDetails: OmsOrderDetail): Date | undefined {
        const status = item.status;
        const statusType = item.statusType;

        if (statusType === OrderStatusType.DELIVERY) {
            if (status === OrderStatus.pendingShipment) {
                return item.estimatedShippingDate;
            } else if (status === OrderStatus.outForDelivery) {
                return item.estimatedDeliveryTime;
            } else {
                const history = orderDetails.orderTimeLineList?.find(
                    (element) =>
                        element.orderId === orderDetails.id &&
                        element.orderStatus === status &&
                        _getStatusType(element.orderType) === statusType
                );
                return history?.createTime;
            }
        } else if (statusType === OrderStatusType.RETURN) {
            if (status === ReturnStatus.RETURN_APPROVED) {
                return item.estimatedReturnDate;
            } else if (status === ReturnStatus.RETURN_PICKUP) {
                return item.estimatedReturnDate;
            } else {
                const history = orderDetails.orderTimeLineList?.find(
                    (element) =>
                        element.orderId === orderDetails.id &&
                        element.orderStatus === status &&
                        _getStatusType(element.orderType) === statusType
                );
                return history?.createTime;
            }
        }
    }


    function _getStatusType(statusType?: string): OrderStatusType | undefined {
        if (statusType === 'delivery') {
            return OrderStatusType.DELIVERY;
        } else if (statusType === 'return') {
            return OrderStatusType.RETURN;
        } else if (statusType === 'exchange') {
            return OrderStatusType.EXCHANGE;
        }
    }
    function isReturnCancelable(order: OmsOrderDetail, orderItemId: number): boolean {
        const item = order.orderItemList?.find((element) => element.id === orderItemId);

        if (!item) {
            return false;
        }

        const returnStatus = item.status;

        if (item.statusType === OrderStatusType.DELIVERY) {
            return false;
        }
        if (returnStatus === ReturnStatus.RETURN_INITIATED || returnStatus === ReturnStatus.RETURN_APPROVED) {
            return true;
        }

        return false;
    }
    return {
        errorLoading,
        orderGeneratedDetails,
        orderParams,
        status,
        days,
        generateOrder,
        // generateConfirmOrder,
        payOrderSuccess,
        isOrderCancelable,
        isReturnCancelable,
        cancelUserOrder,
        confirmReceiveOrder,
        getEstimatedDate,
        isOrderReturnable,
        isOrderCommentable,
        getProductRating,
        reset
    }
})